<template>
  <div
    class="m-2 shadow rounded overflow-hidden group"
    :class="hasErrors ? 'bg-red-50 shadow-outline-red' : 'bg-white'"
  >
    <div class="p-5 flex">
      <div
        class="text-green-400 lg:text-green-300 group-hover:text-green-400 text-xl handle cursor-move"
      >
        <font-awesome-icon icon="grip-vertical"></font-awesome-icon>
      </div>
      <div class="ml-4 w-full">
        <div class="flex flex-col md:flex-row justify-between w-full">
          <div class="flex md:block justify-between">
            <div class="font-medium">
              <span class="text-gray-400" v-if="!type.title">Kein Titel festgelegt</span>
              <span>{{ type.title }}</span>
              <button
                class="ml-2 text-sm text-gray-800 lg:invisible group-hover:visible focus:outline-none"
                @click="open = !open"
                v-show="type.editable"
              >
                <font-awesome-icon :icon="['fad', 'edit']"></font-awesome-icon>
              </button>
            </div>
            <div class="flex text-sm items-center text-gray-600" v-if="beforePublishedFrom">
              <font-awesome-icon
                class="text-yellow-500"
                :icon="['fad', 'alarm-clock']"
              ></font-awesome-icon>
              <span class="ml-2">Verkauf geplant</span>
            </div>
            <div class="flex text-sm items-center text-gray-600" v-else-if="afterPublishedTo">
              <font-awesome-icon
                class="text-red-500"
                :icon="['fad', 'times-circle']"
              ></font-awesome-icon>
              <span class="ml-2">Verkauf beendet</span>
            </div>
            <div class="flex text-sm items-center text-gray-600" v-else>
              <font-awesome-icon
                class="text-green-500"
                :icon="['fad', 'check-circle']"
              ></font-awesome-icon>
              <span class="ml-2">Aktiv</span>
            </div>
          </div>

          <div class="flex mt-1 md:mt-0">
            <div class="mr-4 md:mr-0" v-show="type.published_online">
              <div>
                Online:
                <span class="font-medium">
                  {{ formatCurrency(type.price_online / 100) }}
                </span>
              </div>
              <div v-show="type.editable" class="text-sm text-gray-600">
                inkl. {{ formatCurrency(fee_online / 100) }} Gebühren
              </div>
            </div>
            <div class="md:ml-4" v-show="type.published_agency">
              <div>
                Vorverkaufsstelle:
                <span class="font-medium">
                  {{ formatCurrency(type.price_agency / 100) }}
                </span>
              </div>
              <div class="text-sm text-gray-600" v-show="type.editable">
                inkl. {{ formatCurrency(fee_agency / 100) }} Gebühren
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-show="open" class="bg-gray-50 px-4 py-5 sm:p-6">
      <div class="grid grid-cols-1 row-gap-6 col-gap-4 sm:grid-cols-8">
        <basic-input
          placeholder="z.B. Standard"
          v-model="type.title"
          class="sm:col-span-4"
          label="Titel"
          :error="errors[`${errorKey}.title`]"
        />

        <basic-input
          placeholder="z.B. Schüler und Studenten"
          corner-hint="Optional"
          v-model="type.description"
          class="sm:col-span-4"
          label="Info"
          :error="errors[`${errorKey}.description`]"
        />

        <basic-date-picker
          v-model="type.published_from.utc"
          corner-hint="Optional"
          format="d.m.Y H:i"
          class="sm:col-span-4 lg:col-span-2"
          label="Verkaufsstart"
          :error="errors[`${errorKey}.published_from.utc`]"
        />

        <basic-date-picker
          v-model="type.published_to.utc"
          corner-hint="Optional"
          format="d.m.Y H:i"
          class="sm:col-span-4 lg:col-span-2"
          label="Verkaufsende"
          :error="errors[`${errorKey}.published_to.utc`]"
          :min-date="type.published_from.utc ? type.published_from.utc : new Date()"
        />

        <div class="sm:col-span-6 lg:col-span-4">
          <label class="block text-sm font-medium leading-5 text-gray-700 mb-1">Verkaufsort</label>
          <span class="flex flex-col sm:flex-row flex-1">
            <button
              type="button"
              @click="type.published_online = !type.published_online"
              :class="
                type.published_online
                  ? 'border-green-700 hover:bg-green-400 text-white bg-green-500'
                  : 'bg-white border-gray-300 text-gray-700 hover:text-gray-500 active:bg-gray-100 active:text-gray-700'
              "
              class="relative inline-flex items-center px-4 py-2 rounded-t-md sm:rounded-t-none sm:rounded-l-md border text-sm leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150"
            >
              <font-awesome-icon
                :class="type.published_online ? 'text-white' : 'text-gray-500'"
                class="-ml-1 mr-2"
                :icon="['fas', 'laptop-house']"
              />Online
            </button>
            <button
              type="button"
              @click="type.published_agency = !type.published_agency"
              :class="
                type.published_agency
                  ? 'border-green-700 hover:bg-green-400 text-white bg-green-500'
                  : 'bg-white border-gray-300 text-gray-700 hover:text-gray-500 active:bg-gray-100 active:text-gray-700'
              "
              class="-my-px sm:my-0 sm:-ml-px relative inline-flex items-center px-4 py-2 border text-sm leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150"
            >
              <font-awesome-icon
                :class="type.published_agency ? 'text-white' : 'text-gray-500'"
                class="-ml-1 mr-2"
                :icon="['fas', 'house-return']"
              />Vorverkaufsstelle
            </button>
            <button
              type="button"
              @click="
                type.published_agency || type.published_online
                  ? (type.published_agency = type.published_online = false)
                  : (type.published_agency = type.published_online = true)
              "
              :class="
                !type.published_agency && !type.published_online
                  ? 'border-green-700 hover:bg-green-400 text-white bg-green-500'
                  : 'bg-white border-gray-300 text-gray-700 hover:text-gray-500 active:bg-gray-100 active:text-gray-700'
              "
              class="sm:-ml-px relative inline-flex items-center px-4 py-2 rounded-b-md sm:rounded-b-none sm:rounded-r-md border text-sm leading-5 font-medium focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150"
            >
              <font-awesome-icon
                :class="
                  !type.published_agency && !type.published_online ? 'text-white' : 'text-gray-500'
                "
                class="-ml-1 mr-2"
                :icon="['fas', 'house-user']"
              />Intern
            </button>
          </span>
        </div>

        <div class="sm:col-span-4">
          <label class="block text-sm font-medium leading-5 text-gray-700"
            >Pro Bestellung zulässige Tickets</label
          >

          <div class="flex">
            <basic-input
              v-model="type.min_amount_per_order"
              class="w-full -mr-px"
              hint="Minimum"
              type="number"
              min="1"
              input-class="rounded-none rounded-l-md relative focus:z-10"
              :error="errors[`${errorKey}.min_amount_per_order`]"
            />
            <basic-input
              v-model="type.max_amount_per_order"
              class="w-full"
              hint="Maximum"
              type="number"
              min="1"
              input-class="rounded-none rounded-r-md relative focus:z-10"
              :error="errors[`${errorKey}.max_amount_per_order`]"
            />
          </div>
        </div>

        <div class="sm:col-span-8 xl:col-span-6 grid grid-cols-3 rounded mt-6">
          <div class="p-4 col-start-2 border-r border-l border-t rounded-tl">
            <font-awesome-icon icon="hand-holding-usd" class="text-gray-500"></font-awesome-icon>
            <span class="ml-2">Sie erhalten</span>
          </div>

          <div class="p-4 border-t border-r rounded-tr">
            <font-awesome-icon icon="users" class="text-gray-500"></font-awesome-icon>
            <span class="ml-2">Ticketkunde zahlt</span>
          </div>

          <div class="p-4 border-b border-t border-l rounded-tl">
            <div class>Online</div>
            <div class="text-sm text-gray-600">
              Ticketgebühr: {{ formatCurrency(fee_online / 100) }}
            </div>
          </div>

          <div class="p-4 border-b border-l border-t border-r">
            <basic-currency-input
              v-model="price_online_excluding_fees"
              @blur="setPriceAgencyExcludingFeesIfNull(price_online_excluding_fees)"
              :value-as-integer="true"
              :allow-negative="false"
              placeholder="0,00 €"
            />
          </div>

          <div class="p-4 border-b border-t border-r">
            <basic-currency-input
              v-model="price_online"
              @blur="setPriceAgencyIfNull(price_online)"
              :value-as-integer="true"
              :allow-negative="false"
              placeholder="0,00 €"
              :error="errors[`${errorKey}.price_online`]"
            />
          </div>

          <div class="p-4 border-l border-b rounded-bl">
            <div class>Vorverkaufsstelle</div>
            <div class="text-sm text-gray-600">
              Ticketgebühr: {{ formatCurrency(fee_agency / 100) }}
            </div>
          </div>

          <div class="p-4 border-r border-l border-b">
            <basic-currency-input
              :value-as-integer="true"
              :allow-negative="false"
              placeholder="0,00 €"
              v-model="price_agency_excluding_fees"
            />
          </div>

          <div class="p-4 border-b border-r rounded-br">
            <basic-currency-input
              v-model="price_agency"
              :value-as-integer="true"
              :allow-negative="false"
              placeholder="0,00 €"
              :error="errors[`${errorKey}.price_agency`]"
            />
          </div>
        </div>
      </div>
      <div class="flex mt-6">
        <basic-button class="mr-4" @click="open = false" color="white">Schließen</basic-button>
        <basic-button color="red" design="secondary" @click="destroy()" leading-icon="trash"
          >Löschen</basic-button
        >
      </div>
    </div>
  </div>
</template>

<script>
import BasicDatePicker from '@/components/basic/DatePicker';
import BasicCurrencyInput from '@/components/basic/CurrencyInput';
import DeleteConfirmationModal from '@/components/modals/DeleteConfirmationModal';
import TicketFeeCalculator from '@/components/TicketFeeCalculator';

import _filter from 'lodash/filter';
import _inRange from 'lodash/inRange';
import _sumBy from 'lodash/sumBy';
import _startsWith from 'lodash/startsWith';
import _some from 'lodash/some';
import _isNull from 'lodash/isNull';

import numeral from 'numeral';
import dayjs from 'dayjs';

import typeClient from '@/api/central/types';

export default {
  props: {
    type: {
      required: true,
      type: Object,
    },
    errors: {
      required: true,
      type: Object,
    },
    errorKey: {
      required: true,
      type: String,
    },
    ticketFees: {
      required: true,
      type: Array,
    },
  },

  components: { BasicDatePicker, BasicCurrencyInput },

  data() {
    return {
      open: !!this.type._id,
    };
  },

  computed: {
    beforePublishedFrom() {
      return dayjs.utc().isBefore(dayjs.utc(this.type.published_from.utc));
    },
    afterPublishedTo() {
      return dayjs.utc().isAfter(dayjs.utc(this.type.published_to.utc));
    },
    price_online: {
      set(value) {
        this.type.price_online = value;
      },
      get() {
        return this.type.price_online;
      },
    },
    price_online_excluding_fees: {
      set(value) {
        this.price_online = _isNull(value)
          ? null
          : this.calculatorInstance('online').setBasePrice(value).getFinalPrice();
      },
      get() {
        return _isNull(this.price_online)
          ? null
          : this.calculatorInstance('online').setFinalPrice(this.price_online).getBasePrice();
      },
    },
    price_agency: {
      set(value) {
        this.type.price_agency = value;
      },
      get() {
        return this.type.price_agency;
      },
    },
    price_agency_excluding_fees: {
      set(value) {
        this.price_agency = _isNull(value)
          ? null
          : this.calculatorInstance('agency').setBasePrice(value).getFinalPrice();
      },
      get() {
        return _isNull(this.price_agency)
          ? null
          : this.calculatorInstance('agency').setFinalPrice(this.price_agency).getBasePrice();
      },
    },
    fee_online() {
      return this.price_online - this.price_online_excluding_fees;
    },
    fee_agency() {
      return this.price_agency - this.price_agency_excluding_fees;
    },
    hasErrors() {
      return _some(this.errors, (value, key) => {
        return _startsWith(key, this.errorKey);
      });
    },
  },
  methods: {
    calculatorInstance(channel) {
      return new TicketFeeCalculator(_filter(this.ticketFees, { channel }));
    },

    setPriceAgencyExcludingFeesIfNull(value) {
      _isNull(this.price_agency_excluding_fees) && (this.price_agency_excluding_fees = value);
    },

    setPriceAgencyIfNull(value) {
      _isNull(this.price_agency) && (this.price_agency = value);
    },

    formatCurrency(value) {
      return numeral(value).format('0,0.00 $');
    },

    destroy() {
      let func = () => {
        let promise = this.type.id ? typeClient.destroy(this.type.id) : Promise.resolve(); // Make empty promise if type was not saved on server

        return promise
          .then(async () => {
            await this.$store.dispatch('editor/refresh');

            this.$toasted.global.success();
            this.$emit('destroyed');
          })
          .catch(() => {
            this.$toasted.global.error();
          });
      };

      this.$modal.show(DeleteConfirmationModal, {
        destroyFunc: func,
        header: `${this.type.title || 'Tickettyp'} wirklich löschen?`,
      });
    },
  },
};
</script>
